import React, {useEffect, useState, useRef} from 'react';
import axios from 'axios';
import {useParams} from 'react-router-dom';
import Header from "../../components/Header";
import WrapperBlog from "../../components/WrapperBlog";
import ResponsiveImage from "../../components/ResponsiveImage";
import moment from 'jalali-moment';
import {faClock, faTelevision} from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/Images/logo.svg'
import {toFarsiRelativeTime} from "../../utils/dateUtils";
import {toFarsiNumber, handleCopyToClipboard} from "../../utils/commonUtils";
import {Helmet} from "react-helmet-async";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLink} from '@fortawesome/free-solid-svg-icons';
import {faTelegram, faWhatsapp} from '@fortawesome/free-brands-svg-icons';
import Footer from "../../components/Footer";
import { BLOG_API_BASE, SITE_BASE_URL } from "../../utils/apiConfig";


function convertToPersianDate(isoDate) {
    return moment(isoDate, 'YYYY-MM-DDTHH:mm:ss.SSSZ').locale('fa').format('D MMMM YYYY');
}

function toPersianNumbers(input) {
    const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return input.replace(/\d/g, (m) => persianNumbers[parseInt(m)]);
}

const BlogPost = () => {
    const {slug} = useParams();
    const [blogPost, setBlogPost] = useState({content: '', title: '', image: ''});
    const [toc, setToc] = useState([]);
    const contentRef = useRef();
    const blogUrl = blogPost.slug ? `${SITE_BASE_URL}/blog/${blogPost.slug}` : `${SITE_BASE_URL}/blog`;
    const canonicalUrl = `${blogUrl}/`;

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const res = await axios.get(`${BLOG_API_BASE}/${slug}/`);
                setBlogPost(res.data.post);
            } catch (error) {
                console.error('Error fetching blog:', error);
            }
        };

        fetchPost();
    }, [slug]);

    // After content is rendered, extract H2s
    useEffect(() => {
        const contentElement = contentRef.current;
        if (!contentElement) return;

        const headings = Array.from(contentElement.querySelectorAll('h2'));
        const tocItems = [];

        headings.forEach((heading, index) => {
            const id = `section-${index}`;
            heading.setAttribute('id', id);
            tocItems.push({
                id,
                text: heading.textContent
            });
        });

        setToc(tocItems);
    }, [blogPost.content]);

    const handleCopy = () => {
        handleCopyToClipboard(blogUrl);
    }

    const url = encodeURIComponent(blogUrl);
    const text = encodeURIComponent("بخونش 👇");
    const telegramLink = `http://t.me/share/url?url=${url}&text=${text}`;

    const shareText = encodeURIComponent(`بخونش 👇
${blogUrl}-`);
    const whatsappLink = `http://wa.me/?text=${shareText}`;

    return (
        <>
            <Helmet>
                <title>{blogPost.title} - کدپرو </title>
                <link rel="canonical" href={canonicalUrl}/>
                <meta name="description"
                      content={blogPost.meta_description}/>
            </Helmet>
            <Header/>
            <main className="py-6 w-full mb-5">
                <WrapperBlog className="relative flex flex-col lg:flex-row gap-4 xl:gap-10 justify-between">
                    {/* Table of Contents Sidebar */}
                    {toc.length > 0 && (
                        <aside className="w-full lg:w-1/5 order-0 lg:block hidden sticky top-24 h-fit order-1 lg:order-none">
                            <h2 className="text-lg font-bold text-gray-800 border-b border-gray-300 p-4 mb-5">
                                فهرست مطالب
                            </h2>
                            <ul className="space-y-3 list-none text-md px-4 lg:px-0">
                                {toc.map((item) => (
                                    <li key={item.id}>
                                        <a href={`#${item.id}`} className="text-gray-800 hover:underline">
                                            {item.text}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </aside>
                    )}

                    {/* Blog Content */}
                    <article className="flex-1 w-full mb-5 px-4 order-1 lg:order-none">
                        <div className="rounded-full bg-black text-white inline-block py-2 px-6 text-md mb-5">
                            {blogPost.category}
                        </div>
                        <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl mb-8">{blogPost.h1}</h1>

                        {blogPost.image && (
                            <ResponsiveImage
                                className="w-full max-h-[600px] object-cover rounded-lg mb-8"
                                src={blogPost.image}
                            />
                        )}

                        {blogPost.content != null && (
                            <div
                                ref={contentRef}
                                className="rtl text-base text-lg md:text-2xl lg:text-md text-black rounded-lg leading-relaxed space-y-4"
                                dangerouslySetInnerHTML={{__html: blogPost.content}}
                            />
                        )}
                    </article>

                    {/* Info Sidebar */}
                    <aside className="w-full lg:w-1/5 sticky top-24 h-fit order-2 lg:order-none">
                        <div className="flex items-center justify-center p-5 lg:p-3">
                            <img src={logo} alt="" className="h-12 ml-3"/>
                            <span className="text-lg md:text-2xl lg:text-md font-bold opacity-80">تیم کدپرو</span>
                        </div>

                        <div className="p-5 lg:p-3 border-t border-gray-300">
                            <div className="flex justify-center mb-4 items-center">
                                <span className="opacity-80 text-lg md:text-2xl lg:text-md font-bold">آخرین برروزرسانی</span>
                            </div>
                            <div className="flex justify-center items-center">
                                <span className="text-md md:text-2xl lg:text-md">{toFarsiRelativeTime(blogPost.updated_at)}</span>
                            </div>
                        </div>

                        <div className="p-5 lg:p-3 border-t border-gray-300">
                            <div className="flex justify-center mb-4 items-center">
                                <span className="text-lg md:text-2xl lg:text-md opacity-80 font-bold">زمان برای خواندن</span>
                            </div>
                            <div className="flex justify-center items-center">
                                <span className="text-lg md:text-2xl lg:text-md">{toFarsiNumber(blogPost.read_time)} دقیقه</span>
                            </div>
                        </div>

                        <div className="p-5 lg:p-3 border-t border-gray-300 mt-5 lg:mt-0">
                            <div className="flex justify-center mb-5 items-center">
                                <span className="text-lg md:text-2xl lg:text-md opacity-80 font-bold">اشتراک گذاری</span>
                            </div>
                            <ul className="flex justify-center gap-10 lg:gap-6 items-center list-none text-md">
                                <li><span onClick={handleCopy}><FontAwesomeIcon icon={faLink}
                                                                                className="h-10 lg:h-6 hover:opacity-70 transition active:h-5"/></span>
                                </li>
                                <li><a href={telegramLink} target="_blank"><FontAwesomeIcon icon={faTelegram}
                                                                                            className="h-10 lg:h-7 hover:opacity-70 transition active:h-5"/></a>
                                </li>
                                <li><a href={whatsappLink} target="_blank"><FontAwesomeIcon icon={faWhatsapp}
                                                                                            className="lg:h-7 h-10 hover:opacity-70 transition active:h-5"/></a>
                                </li>
                            </ul>
                        </div>
                    </aside>
                </WrapperBlog>
            </main>
            <Footer/>
        </>
    );
};

export default BlogPost;
